import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Users, DollarSign, Receipt } from 'lucide-react';
import apiClient from '@/services/api';
import type { SalesSummaryReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Server Sales: per-server orders, tips, total and average check. */
export default function ServerSalesReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<SalesSummaryReport>({
    queryKey: ['sales-summary', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getSalesSummary(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const servers = data?.byServer ?? [];

  // --- Server filter (client-side, display-only) ---------------------------
  // selectedServers = [] means "all" (sentinel). Checking specific servers
  // narrows the table + KPIs to just those. Following the FoodVsDrinkReport
  // pattern: the API already returns every server, so filtering never refetches.
  const [selectedServers, setSelectedServers] = useState<string[]>([]);
  const serverOptions = [...new Set(servers.map((s) => s.serverName))].sort((a, b) => a.localeCompare(b));

  // Drop stale selections when the server list changes (e.g. restaurant switch).
  useEffect(() => {
    if (selectedServers.length === 0) return;
    const valid = new Set(serverOptions);
    const pruned = selectedServers.filter((name) => valid.has(name));
    if (pruned.length !== selectedServers.length) setSelectedServers(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverOptions.join('|')]);

  const allServers = selectedServers.length === 0;
  const visibleServers = allServers
    ? servers
    : servers.filter((s) => selectedServers.includes(s.serverName));

  const toggleServer = (name: string) => {
    setSelectedServers((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  // --- KPIs (recomputed from filtered set) ---------------------------------
  const totalSales = visibleServers.reduce((sum, s) => sum + Number(s.total ?? 0), 0);
  const totalOrders = visibleServers.reduce((sum, s) => sum + Number(s.orders ?? 0), 0);
  const totalTips = visibleServers.reduce((sum, s) => sum + Number(s.tip ?? 0), 0);
  const avgCheck = totalOrders > 0 ? totalSales / totalOrders : 0;
  const check = (total: number, orders: number) => (orders > 0 ? total / orders : 0);

  const meta = () => ({
    restaurantName: selectedRestaurant?.name ?? '',
    startDate,
    endDate,
    formatCurrency,
  });

  const sections = (): ReportSection[] => [
    {
      heading: 'Server Sales',
      headers: ['Server', 'Orders', 'Avg Check', 'Tips', 'Total'],
      rows: visibleServers.map((s) => [
        s.serverName,
        s.orders,
        formatCurrency(check(Number(s.total), s.orders)),
        formatCurrency(s.tip),
        formatCurrency(s.total),
      ]),
    },
  ];

  const kpis = (): [string, string][] => [
    ['Servers', visibleServers.length.toLocaleString()],
    ['Total Sales', formatCurrency(totalSales)],
    ['Avg Check', formatCurrency(avgCheck)],
  ];

  return (
    <ReportFrame
      title="Server Sales"
      description="Per-server orders, tips, total and average check across the range."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={servers.length === 0}
      exportDisabled={!data}
      onExportCsv={() =>
        data &&
        downloadCsv(
          `server-sales-${format(new Date(), 'yyyy-MM-dd')}.csv`,
          buildGenericCsv('Server Sales', meta(), kpis(), sections()),
        )
      }
      onExportPdf={() => data && printGenericReport('Server Sales Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label="Servers" value={visibleServers.length.toLocaleString()} icon={Users} palette="blue" />
        <KPICard label="Total Sales" value={formatCurrency(totalSales)} icon={DollarSign} palette="green" />
        <KPICard label="Avg Check" value={formatCurrency(avgCheck)} icon={Receipt} palette="purple" />
      </div>

      {/* Server filter checkboxes — [] means "all". Checking names narrows the view. */}
      {serverOptions.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Filter by server</h3>
              <span className="text-xs text-gray-500">
                {allServers ? 'All servers' : `${selectedServers.length} of ${serverOptions.length} selected`}
              </span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {serverOptions.map((name) => (
                <label key={name} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allServers || selectedServers.includes(name)}
                    onChange={() => toggleServer(name)}
                    className="rounded border-gray-300"
                  />
                  {name}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-semibold mb-4">By Server</h3>
          <SimpleTable
            headers={['Server', 'Orders', 'Avg Check', 'Tips', 'Total']}
            rows={visibleServers.map((s) => [
              s.serverName,
              s.orders,
              formatCurrency(check(Number(s.total), s.orders)),
              formatCurrency(s.tip),
              formatCurrency(s.total),
            ])}
            footer={[
              'Total',
              totalOrders,
              formatCurrency(avgCheck),
              formatCurrency(totalTips),
              formatCurrency(totalSales),
            ]}
          />
        </div>
      </div>
    </ReportFrame>
  );
}
